import React, { useState, useEffect } from "react";
import { Input, message, Tag, Select, DatePicker } from "antd";
import {
  SearchOutlined,
  PhoneOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  SwapOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { voiceIpAPIWithErrorHandling } from "../../../api/voiceIP/voiceIP";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";

const { RangePicker } = DatePicker;

// Định nghĩa trạng thái cuộc gọi với màu sắc và nhãn
const CALL_STATUS = {
  answered: { label: "Đã trả lời", color: "success" },
  busy: { label: "Máy bận", color: "warning" },
  failed: { label: "Thất bại", color: "error" },
  cancel: { label: "Đã hủy", color: "default" },
  "no-answer": { label: "Không trả lời", color: "warning" },
  "not-available": { label: "Không có sẵn", color: "default" },
  ivr: { label: "IVR", color: "processing" },
  "busy-line": { label: "Đường dây bận", color: "warning" },
  voicemail: { label: "Hộp thư thoại", color: "default" },
  "invalid-number": { label: "Số không hợp lệ", color: "error" },
  "phone-block": { label: "Số bị chặn", color: "error" },
  congestion: { label: "Mạng tắc nghẽn", color: "error" },
  "out-time": { label: "Ngoài giờ", color: "default" },
  "in-time": { label: "Trong giờ", color: "processing" },
  "press-2-meet": { label: "Nhấn 2 - Gặp", color: "success" },
  "press-2-notmeet": { label: "Nhấn 2 - Không gặp", color: "warning" },
  "press-1": { label: "Nhấn 1", color: "processing" },
  "miss-call": { label: "Nhỡ", color: "error" },
  "miss-call-press": { label: "Nhỡ có nhấn phím", color: "warning" },
  drop: { label: "Từ chối", color: "error" },
  dnc: { label: "DNC", color: "error" },
  default: { label: "Mặc định", color: "default" },
};

// Định nghĩa chiều cuộc gọi
const CALL_DIRECTION = {
  inbound: {
    label: "Gọi đến",
    color: "blue",
    icon: <ArrowLeftOutlined />,
  },
  outbound: {
    label: "Gọi đi",
    color: "green",
    icon: <ArrowRightOutlined />,
  },
  local: {
    label: "Nội bộ",
    color: "purple",
    icon: <SwapOutlined />,
  },
};

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = [
  "status",
  "direction",
  "time_started",
  "time_ended",
  "recording_url",
  "summary",
];

export default function VoiceHistories() {
  // State management
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedStatus, setSelectedStatus] = useState(undefined);

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data
  const loadData = async (params = {}) => {
    try {
      setLoading(true);

      // Prepare date params
      const dateParams = {};
      if (dateRange[0] && dateRange[1]) {
        dateParams.start_date = dateRange[0].format("YYYY-MM-DD");
        dateParams.end_date = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await voiceIpAPIWithErrorHandling.getHistories({
        page: pagination.current,
        per_page: pagination.pageSize,
        search: searchText,
        status: selectedStatus,
        ...dateParams,
        ...params,
      });

      if (response.success) {
        const { data: responseData } = response;
        setData(responseData.data);
        setPagination({
          ...pagination,
          pageSize: responseData.per_page,
          current: responseData.current_page,
          total: responseData.total,
        });
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    loadData({ page: 1 });
  };

  // Handle status change
  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    setPagination((p) => ({ ...p, current: 1 }));
    loadData({ page: 1, status: value });
  };

  // Handle date range change
  const handleDateChange = (dates) => {
    const range = dates || [null, null];
    setDateRange(range);
    setPagination((p) => ({ ...p, current: 1 }));
    const dateParams =
      range[0] && range[1]
        ? {
            start_date: range[0].format("YYYY-MM-DD"),
            end_date: range[1].format("YYYY-MM-DD"),
          }
        : { start_date: undefined, end_date: undefined };
    loadData({ page: 1, ...dateParams });
  };

  // Handle reset
  const handleReset = () => {
    setSearchText("");
    setDateRange([null, null]);
    setSelectedStatus(undefined);
    setPagination({ ...pagination, current: 1 });
    loadData({
      page: 1,
      search: "",
      status: undefined,
      direction: undefined,
      start_date: undefined,
      end_date: undefined,
    });
  };

  // Handle pagination change
  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    loadData({
      page: newPagination.current,
      per_page: newPagination.pageSize,
    });
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "index", label: "STT", fixed: true,
      column: {
        title: "STT", key: "index", width: 60, fixed: "left", align: "center",
        render: (_, record, index) =>
          (pagination.current - 1) * pagination.pageSize + index + 1,
      },
    },
    {
      key: "from_number", label: "Số gọi đi", fixed: true,
      column: {
        title: "Số gọi đi", dataIndex: "from_number", key: "from_number",
        fixed: "left", width: 130,
        render: (from_number) => from_number || "-",
      },
    },
    {
      key: "to_number", label: "Số nhận", fixed: true,
      column: {
        title: "Số nhận", dataIndex: "to_number", key: "to_number",
        fixed: "left", width: 130,
        render: (to_number) => to_number || "-",
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 150, align: "center",
        render: (status) => {
          const statusConfig = CALL_STATUS[status] || CALL_STATUS.default;
          return <Tag color={statusConfig.color}>{statusConfig.label}</Tag>;
        },
      },
    },
    {
      key: "direction", label: "Chiều cuộc gọi",
      column: {
        title: "Chiều cuộc gọi", dataIndex: "direction", key: "direction", width: 130, align: "center",
        render: (direction) => {
          const dir = CALL_DIRECTION[direction] || {
            label: direction || "-",
            color: "default",
            icon: <PhoneOutlined />,
          };
          return (
            <Tag color={dir.color} icon={dir.icon}>
              {dir.label}
            </Tag>
          );
        },
      },
    },
    {
      key: "time_started", label: "Bắt đầu",
      column: {
        title: "Bắt đầu", dataIndex: "time_started", key: "time_started", width: 200,
        render: (time_started) =>
          time_started ? dayjs(time_started).format("DD/MM/YYYY HH:mm:ss") : "-",
      },
    },
    {
      key: "time_ended", label: "Kết thúc",
      column: {
        title: "Kết thúc", dataIndex: "time_ended", key: "time_ended", width: 200,
        render: (time_ended) =>
          time_ended ? dayjs(time_ended).format("DD/MM/YYYY HH:mm:ss") : "-",
      },
    },
    {
      key: "recording_url", label: "File ghi âm",
      column: {
        title: "File ghi âm", dataIndex: "recording_url", key: "recording_url", width: 300, ellipsis: true,
        render: (text) => {
          if (!text) return "-";
          return (
            <audio controls style={{ width: "100%" }}>
              <source src={text} type="audio/mpeg" />
              Trình duyệt của bạn không hỗ trợ phát âm thanh.
            </audio>
          );
        },
      },
    },
    {
      key: "summary", label: "Nội dung tóm tắt",
      column: {
        title: "Nội dung tóm tắt", dataIndex: "summary", key: "summary", width: 300, ellipsis: true,
        render: (text) => {
          if (!text) return "-";

          const handleCopy = () => {
            navigator.clipboard.writeText(text);
            message.success("Đã sao chép nội dung!");
          };

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {text}
              </span>
              <CopyOutlined
                onClick={handleCopy}
                style={{ cursor: "pointer", color: "#0070D7" }}
              />
            </div>
          );
        },
      },
    },
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 6 },
      field: (
        <Input
          placeholder="Nhập để tìm kiếm..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
          prefix={<SearchOutlined />}
        />
      ),
    },
    {
      key: "status", label: "Trạng thái", span: { xs: 24, sm: 12, md: 6 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Chọn trạng thái"
          value={selectedStatus}
          onChange={handleStatusChange}
          allowClear
        >
          {Object.entries(CALL_STATUS).map(([key, value]) => (
            <Select.Option key={key} value={key}>
              {value.label}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      key: "dateRange", label: "Khoảng thời gian", span: { xs: 24, sm: 12, md: 6 },
      field: (
        <RangePicker
          style={{ width: "100%" }}
          format="DD/MM/YYYY"
          placeholder={["Từ ngày", "Đến ngày"]}
          value={dateRange}
          onChange={handleDateChange}
        />
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || selectedStatus || (dateRange[0] && dateRange[1]));

  return (
    <BrandedPageLayout
      title="Lịch sử cuộc gọi"
      subtitle="Theo dõi lịch sử cuộc gọi, trạng thái và bản ghi âm."
      breadcrumbItems={[{ title: "Lịch sử cuộc gọi" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => loadData()}
      />

      <ListTableCard
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có lịch sử cuộc gọi"
      />
    </BrandedPageLayout>
  );
}
